import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type RefObject,
} from 'react';
import { PauseIcon, PlayIcon } from '../components/icons';
import { Banner, cx } from '../components/ui';
import { timecode } from '../lib/format';
import type { Playback } from '../lib/types';
import type HlsType from 'hls.js';
import { useHotkeys } from '../lib/useHotkeys';

/**
 * Playback.
 *
 * Safari (and every iOS browser, which is Safari underneath) plays HLS
 * natively and does it better than any JavaScript can, so it gets the URL
 * directly. Everyone else gets hls.js. If neither works we fall back to the
 * progressive MP4, which the pipeline always produces.
 *
 * The controls are custom rather than native because playback speed is a
 * first-class control for this product — people watch their colleagues'
 * updates at 1.5× — and because a share page is a piece of Slate's surface,
 * not a browser default.
 */

const SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const;

export interface PlayerProps {
  playback: Playback;
  title: string;
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Fires roughly every 250ms while playing. */
  onProgress?: (currentTime: number, duration: number) => void;
  onEnded?: () => void;
}

export function Player({ playback, title, videoRef, onProgress, onEnded }: PlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [rate, setRate] = useState(1);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(true);
  const hideTimerRef = useRef<number | null>(null);

  // ---- source ---------------------------------------------------------------
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setError(null);

    const { hlsUrl, mp4Url } = playback;

    if (hlsUrl && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      return;
    }

    if (hlsUrl) {
      // hls.js is ~590kB — a third of the bundle — and only this component
      // ever needs it. Loading it here keeps it off the recorder and dashboard,
      // which are the pages people open most and where it would never be used.
      let instance: HlsType | null = null;
      let cancelled = false;

      void (async () => {
        const { default: Hls } = await import('hls.js');
        if (cancelled) return;

        if (!Hls.isSupported()) {
          if (mp4Url) video.src = mp4Url;
          else setError('This browser cannot play this video.');
          return;
        }

        instance = new Hls({ enableWorker: true, backBufferLength: 60 });
        instance.loadSource(hlsUrl);
        instance.attachMedia(video);
        instance.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal || !instance) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            instance.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            instance.recoverMediaError();
          } else if (mp4Url) {
            instance.destroy();
            instance = null;
            video.src = mp4Url;
          } else {
            instance.destroy();
            instance = null;
            setError('This video would not play. Try reloading the page.');
          }
        });
      })();

      return () => {
        cancelled = true;
        instance?.destroy();
      };
    }

    if (mp4Url) {
      video.src = mp4Url;
      return;
    }

    setError('There is no playable version of this video yet.');
    return;
  }, [playback, videoRef]);

  // ---- element events -------------------------------------------------------
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onLoaded = () => setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    const onTime = () => {
      setCurrentTime(video.currentTime);
      onProgress?.(video.currentTime, video.duration);
      if (video.buffered.length > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1));
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onRate = () => setRate(video.playbackRate);
    const onVolume = () => {
      setMuted(video.muted);
      setVolume(video.volume);
    };
    const onEnd = () => {
      setPlaying(false);
      onEnded?.();
    };
    const onFail = () =>
      setError('This video would not play. It may still be processing — try reloading.');

    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('durationchange', onLoaded);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ratechange', onRate);
    video.addEventListener('volumechange', onVolume);
    video.addEventListener('ended', onEnd);
    video.addEventListener('error', onFail);

    return () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('durationchange', onLoaded);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ratechange', onRate);
      video.removeEventListener('volumechange', onVolume);
      video.removeEventListener('ended', onEnd);
      video.removeEventListener('error', onFail);
    };
  }, [onEnded, onProgress, videoRef]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ---- commands -------------------------------------------------------------
  const toggle = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => setError('Your browser blocked playback. Press play again.'));
    } else {
      video.pause();
    }
  }, [videoRef]);

  const seekBy = useCallback(
    (delta: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = Math.max(0, Math.min(video.duration || 0, video.currentTime + delta));
    },
    [videoRef],
  );

  const seekToFraction = useCallback(
    (fraction: number) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(video.duration)) return;
      video.currentTime = video.duration * fraction;
    },
    [videoRef],
  );

  const setSpeed = useCallback(
    (value: number) => {
      const video = videoRef.current;
      if (video) video.playbackRate = value;
    },
    [videoRef],
  );

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
    } else {
      void container.requestFullscreen().catch(() => {
        // iPhone Safari only fullscreens the video element itself.
        const video = videoRef.current as (HTMLVideoElement & {
          webkitEnterFullscreen?: () => void;
        }) | null;
        video?.webkitEnterFullscreen?.();
      });
    }
  }, [videoRef]);

  useHotkeys({
    space: toggle,
    k: toggle,
    arrowleft: () => seekBy(-5),
    arrowright: () => seekBy(5),
    j: () => seekBy(-10),
    l: () => seekBy(10),
    m: () => {
      const video = videoRef.current;
      if (video) video.muted = !video.muted;
    },
    f: toggleFullscreen,
    '0': () => seekToFraction(0),
    '1': () => seekToFraction(0.1),
    '2': () => seekToFraction(0.2),
    '3': () => seekToFraction(0.3),
    '4': () => seekToFraction(0.4),
    '5': () => seekToFraction(0.5),
    '6': () => seekToFraction(0.6),
    '7': () => seekToFraction(0.7),
    '8': () => seekToFraction(0.8),
    '9': () => seekToFraction(0.9),
  });

  // ---- chrome auto-hide -----------------------------------------------------
  const wake = useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 2600);
  }, [videoRef]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  const onScrub = (event: ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Number(event.target.value);
    setCurrentTime(Number(event.target.value));
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration > 0 ? (buffered / duration) * 100 : 0;

  return (
    <div className="space-y-3">
      <div
        ref={containerRef}
        onMouseMove={wake}
        onTouchStart={wake}
        className="group relative overflow-hidden rounded-md border border-hairline bg-black"
      >
        <video
          ref={videoRef}
          playsInline
          preload="metadata"
          poster={playback.posterUrl ?? undefined}
          onClick={toggle}
          className="aspect-video w-full cursor-pointer bg-black"
          crossOrigin={playback.captionsUrl ? 'anonymous' : undefined}
        >
          {playback.captionsUrl ? (
            <track
              kind="captions"
              srcLang="en"
              label="English"
              src={playback.captionsUrl}
              default
            />
          ) : null}
        </video>

        {!playing ? (
          <button
            type="button"
            onClick={toggle}
            aria-label={`Play ${title}`}
            className="absolute inset-0 flex items-center justify-center bg-slate-950/25 transition-colors duration-120 hover:bg-slate-950/35"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-950/80 text-chalk">
              <PlayIcon size={26} />
            </span>
          </button>
        ) : null}

        <div
          className={cx(
            'absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 to-transparent px-3 pb-2.5 pt-8 transition-opacity duration-200',
            showControls || !playing ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <div className="relative mb-2 h-1.5">
            <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk/20" />
            <div
              className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk/30"
              style={{ width: `${bufferedPercent}%` }}
            />
            <div
              className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk"
              style={{ width: `${progressPercent}%` }}
            />
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              onChange={onScrub}
              aria-label="Seek"
              className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent opacity-0"
            />
          </div>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={toggle}
              aria-label={playing ? 'Pause' : 'Play'}
              className="text-chalk transition-opacity duration-120 hover:opacity-80"
            >
              {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
            </button>

            <span className="slate-type text-micro text-chalk" data-timecode>
              {timecode(currentTime)} <span className="text-chalk-faint">/ {timecode(duration)}</span>
            </span>

            <div className="ml-auto flex items-center gap-2">
              <label className="hidden items-center gap-1.5 sm:flex">
                <span className="sr-only">Volume</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={muted ? 0 : volume}
                  onChange={(event) => {
                    const video = videoRef.current;
                    if (!video) return;
                    video.volume = Number(event.target.value);
                    video.muted = Number(event.target.value) === 0;
                  }}
                  className="h-1 w-16 cursor-pointer appearance-none rounded-full bg-chalk/30 accent-chalk"
                />
              </label>

              <select
                value={rate}
                onChange={(event) => setSpeed(Number(event.target.value))}
                aria-label="Playback speed"
                className="h-7 rounded-xs border border-chalk/20 bg-slate-950/70 px-1.5 font-mono text-micro tnum text-chalk"
              >
                {SPEEDS.map((speed) => (
                  <option key={speed} value={speed}>
                    {speed}×
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={toggleFullscreen}
                aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
                className="rounded-xs border border-chalk/20 px-1.5 py-1 font-mono text-micro text-chalk"
              >
                {fullscreen ? 'Exit' : 'Full'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error ? <Banner tone="danger" title="Playback problem">{error}</Banner> : null}

      <p className="hidden text-caption text-chalk-faint md:block">
        Space or K play and pause · ← → jump 5 seconds · J L jump 10 · M mutes · F is full screen
      </p>
    </div>
  );
}
